import React, { useCallback, useEffect, useState } from 'react';
import { Building2, Loader as Loader2, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { grantCompanyAccess, listCompanyAccess, revokeCompanyAccess } from '../../lib/auth';
import type { CompanyOption } from '../../types';

interface CompanyAccessModalProps {
  adminId: string;
  adminName: string;
  onClose: () => void;
}

export function CompanyAccessModal({ adminId, adminName, onClose }: CompanyAccessModalProps) {
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [granted, setGranted] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const result = await listCompanyAccess(adminId);
    if (!result.success || !result.data) {
      setError(result.error || 'Failed to load company access.');
      setIsLoading(false);
      return;
    }
    setCompanies(result.data.companies.filter(c => c.id !== adminId));
    setGranted(new Set(result.data.granted));
    setIsLoading(false);
  }, [adminId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (companyId: string, isGranted: boolean) => {
    setPendingId(companyId);
    setError(null);

    const result = isGranted
      ? await revokeCompanyAccess(adminId, companyId)
      : await grantCompanyAccess(adminId, companyId);

    if (!result.success) {
      setError(result.error || 'Failed to update company access.');
      setPendingId(null);
      return;
    }

    setGranted(prev => {
      const next = new Set(prev);
      if (isGranted) next.delete(companyId);
      else next.add(companyId);
      return next;
    });
    setPendingId(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-start justify-between border-b border-gray-200 p-6 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Company access
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Choose which companies {adminName} can switch to and administer.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : companies.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              There are no other active companies to grant.
            </p>
          ) : (
            <ul className="space-y-2">
              {companies.map(company => {
                const isGranted = granted.has(company.id);
                const isPending = pendingId === company.id;
                return (
                  <li
                    key={company.id}
                    className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700"
                  >
                    <div className="flex min-w-0 items-center">
                      <Building2 className="mr-3 h-4 w-4 flex-shrink-0 text-gray-400" />
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                          {company.company_name || company.full_name}
                        </div>
                        <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {company.email}
                        </div>
                      </div>
                    </div>
                    <Button
                      onClick={() => handleToggle(company.id, isGranted)}
                      variant={isGranted ? 'danger' : 'success'}
                      size="sm"
                      disabled={!!pendingId}
                    >
                      {isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : isGranted ? (
                        'Revoke'
                      ) : (
                        'Grant'
                      )}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex justify-end border-t border-gray-200 p-4 dark:border-gray-700">
          <Button onClick={onClose} variant="secondary" size="sm">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
